<template>
  <div class="rcs-step">
    <h2 class="rcs-step__heading">
      {{ t('storeParameters.services.rcsServiceParameter.agentsList.title') }}
    </h2>
    <p class="rcs-step__lead">
      {{ t('storeParameters.services.rcsServiceParameter.agentsList.subtitle') }}
    </p>

    <div class="rcs-agents-grid">
      <!-- Shared Shopimind agent: always available, not editable. -->
      <div class="rcs-agent-card rcs-agent-card--shared">
        <div class="rcs-agent-card__icon">
          <i class="fa-solid fa-bolt" />
        </div>
        <p class="rcs-agent-card__title">
          {{ sharedSender.display_name }}
        </p>
        <p class="rcs-agent-card__subtitle">
          {{ t('storeParameters.services.rcsServiceParameter.statusScreen.senderId') }}:
          {{ sharedSender.sender_id }}
        </p>
        <span class="rcs-agent-card__badge rcs-agent-card__badge--active">
          <i class="fa-solid fa-circle-check" />
          {{ t('storeParameters.services.rcsServiceParameter.agentsList.badges.active') }}
        </span>
      </div>

      <!-- Dedicated agents (drafts included). -->
      <div
        v-for="agent in agents"
        :key="agent.local_id"
        class="rcs-agent-card"
        :class="[
          `rcs-agent-card--${cardState(agent)}`,
          { 'rcs-agent-card--clickable': isEditable(agent) },
        ]"
        :role="isEditable(agent) ? 'button' : undefined"
        :tabindex="isEditable(agent) ? 0 : undefined"
        @click="isEditable(agent) && $emit('edit', agent.local_id)"
        @keydown.enter="isEditable(agent) && $emit('edit', agent.local_id)"
      >
        <button
          v-if="canDeleteAgent(agent)"
          type="button"
          class="rcs-agent-card__delete"
          :disabled="loading"
          :title="t('storeParameters.services.rcsServiceParameter.agentsList.actions.delete')"
          @click.stop="$emit('delete', agent.local_id)"
        >
          <i class="fa-solid fa-trash-can" />
        </button>

        <div class="rcs-agent-card__icon">
          <i class="fa-solid fa-id-card-clip" />
        </div>
        <p class="rcs-agent-card__title">
          {{ agent.display_name
            || t('storeParameters.services.rcsServiceParameter.statusScreen.unknownAgent') }}
        </p>
        <p class="rcs-agent-card__subtitle">
          {{ t('storeParameters.services.rcsServiceParameter.statusScreen.senderId') }}:
          {{ agent.sender_id || '—' }}
        </p>
        <div class="rcs-agent-card__badges">
          <span
            class="rcs-agent-card__badge"
            :class="`rcs-agent-card__badge--${cardState(agent)}`"
          >
            <i :class="badgeIcon(agent)" />
            {{ t(`storeParameters.services.rcsServiceParameter.agentsList.badges.${cardState(agent)}`) }}
          </span>
          <span
            v-if="isPendingSync(agent)"
            class="rcs-agent-card__badge rcs-agent-card__badge--pendingChanges"
          >
            <i class="fa-solid fa-cloud-arrow-up" />
            {{ t('storeParameters.services.rcsServiceParameter.agentsList.badges.pendingChanges') }}
          </span>
        </div>

        <div
          v-if="isPendingSync(agent) && isEditable(agent)"
          class="rcs-agent-card__actions"
        >
          <button
            type="button"
            class="p-button p-component p-button-sm p-button-outlined rcs-agent-card__edit"
            :disabled="loading"
            @click.stop="$emit('edit', agent.local_id)"
          >
            <i class="fa-solid fa-pen-to-square" />
            {{ t('storeParameters.services.rcsServiceParameter.agentsList.actions.editAgent') }}
          </button>
          <button
            type="button"
            class="p-button p-component p-button-sm rcs-agent-card__push"
            :disabled="loading"
            @click.stop="$emit('push', agent.local_id)"
          >
            <i class="fa-solid fa-cloud-arrow-up" />
            {{ pushActionLabel(agent) }}
          </button>
        </div>
        <button
          v-else-if="isPendingSync(agent)"
          type="button"
          class="p-button p-component p-button-sm rcs-agent-card__push"
          :disabled="loading"
          @click.stop="$emit('push', agent.local_id)"
        >
          <i class="fa-solid fa-cloud-arrow-up" />
          {{ pushActionLabel(agent) }}
        </button>
        <button
          v-else-if="cardState(agent) === 'rejected' || cardState(agent) === 'failed'"
          type="button"
          class="p-button p-component p-button-sm rcs-agent-card__retry"
          :disabled="loading"
          @click.stop="$emit('retry', agent.local_id)"
        >
          <i class="fa-solid fa-rotate-right" />
          {{ t('storeParameters.services.rcsServiceParameter.statusScreen.retry') }}
        </button>
        <p
          v-else-if="isEditable(agent)"
          class="rcs-step__hint rcs-agent-card__hint"
        >
          <i class="fa-solid fa-pen-to-square" />
          {{ t('storeParameters.services.rcsServiceParameter.agentsList.editHint') }}
        </p>
      </div>

      <!-- Create a new dedicated agent. -->
      <button
        type="button"
        class="rcs-agent-card rcs-agent-card--create"
        :disabled="loading"
        @click="$emit('create')"
      >
        <div class="rcs-agent-card__icon">
          <i class="fa-solid fa-plus" />
        </div>
        <p class="rcs-agent-card__title">
          {{ t('storeParameters.services.rcsServiceParameter.agentsList.createTitle') }}
        </p>
        <p class="rcs-agent-card__subtitle">
          {{ t('storeParameters.services.rcsServiceParameter.agentsList.createSubtitle') }}
        </p>
        <small class="rcs-step__hint">
          <i class="fa-solid fa-clock" />
          {{ t('storeParameters.services.rcsServiceParameter.senderChoice.dedicated.duration') }}
        </small>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  RcsAgent,
  RcsAgentCardState,
  RcsSelectableSender,
  getRcsAgentCardState,
  isRcsAgentAwaitingPublish,
  isRcsAgentDeletable,
  isRcsAgentEditable,
  isRcsAgentPendingRemoteSync,
} from '@/types/rcs-onboarding';

const BADGE_ICONS: Record<RcsAgentCardState, string> = {
  active: 'fa-solid fa-circle-check',
  pending: 'fa-solid fa-clock',
  draft: 'fa-solid fa-file-pen',
  rejected: 'fa-solid fa-circle-xmark',
  failed: 'fa-solid fa-triangle-exclamation',
};

export default defineComponent({
  name: 'RcsAgentsListStep',

  props: {
    sharedSender: {
      type: Object as PropType<RcsSelectableSender>,
      required: true,
    },
    agents: {
      type: Array as PropType<RcsAgent[]>,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['create', 'edit', 'delete', 'retry', 'push'],

  setup() {
    const { t } = useI18n();
    const cardState = (agent: RcsAgent): RcsAgentCardState => getRcsAgentCardState(agent);
    const isEditable = (agent: RcsAgent): boolean => isRcsAgentEditable(agent);
    const badgeIcon = (agent: RcsAgent): string => BADGE_ICONS[getRcsAgentCardState(agent)];
    const isPendingSync = (agent: RcsAgent): boolean => (
      isRcsAgentPendingRemoteSync(agent)
    );
    const canDeleteAgent = (agent: RcsAgent): boolean => isRcsAgentDeletable(agent);
    const pushActionLabel = (agent: RcsAgent): string => (
      isRcsAgentAwaitingPublish(agent)
        ? t('storeParameters.services.rcsServiceParameter.agentsList.actions.publishAgent')
        : t('storeParameters.services.rcsServiceParameter.agentsList.actions.applyChanges')
    );
    return {
      t,
      cardState,
      isEditable,
      badgeIcon,
      isPendingSync,
      canDeleteAgent,
      pushActionLabel,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../rcs-onboarding.scss';
</style>
